import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("span", null, "La modification d'un utilisateur permet de modifier les informations suivantes:", -1 /* HOISTED */)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("ul", { class: "q-mb-none" }, [
  /*#__PURE__*/_createElementVNode("li", { id: "username_example" }, " Le nom d'utilisateur (prénom, prénom nom, prénom et initiale de nom, etc.) ")
], -1 /* HOISTED */)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "q-mt-sm text-caption q-mx-sm q-mb-none q-pb-none" }, " * : champs obligatoires ", -1 /* HOISTED */)

import { useFocus } from '@vueuse/core';
import type { PropType } from 'vue';
import { ref, computed, toRefs, watch } from 'vue';
import { useQuasar, QInput } from 'quasar';

// PERSONAL COMPONENTS
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';
import QInputUsername from '@components/form/QInputUsername.vue';
import QInputEmail from '@components/form/QInputEmail.vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// PERSONAL LIBS
import { RoleEnum } from '@shared/enum/role.enum';
import { IUser } from '@shared/interface/model.interface';
import { $t } from '@i18n/utils';
import {
  displayDialogError,
  displayNotifSuccess,
} from '@shared/lib/notify.lib';
import adminRepository from '@data/repository/admin.repository';
import { UserStatusEnum } from '@shared/enum/user.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

/**
 * Events
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'DialogUserUpdate',
  props: {
  show: { type: Boolean, required: true, default: false },
  user: { type: Object as PropType<IUser>, required: true }
},
  emits: ['close', 'success', 'failed'],
  setup(__props, { emit: __emit }) {

// VENDOR
const emits = __emit;

/**
 * Globals
 */
const $q = useQuasar();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * HTMLElement ref
 */
const qInputEmailRef = ref();
const qInputUsernameRef = ref();

/**
 * Reactive datas
 */
const loadingPassword = ref<boolean>(false);
const loadingDialog = ref<boolean>(false);
const username = ref<string | undefined>(undefined);
const email = ref<string | undefined>(undefined);
const userIsActiveToggle = ref<boolean>(false);

/**
 * Computed properties
 */
const connectedUser = computed(() => sessionManager.currentUser.value);
const isSuperAdmin = computed<boolean>(
  () => user.value?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN) === true,
);
const updatesMade = computed<boolean>(() => {
  return (
    (userIsActiveToggle.value === true &&
      user.value.status !== UserStatusEnum.ACTIVE) ||
    (userIsActiveToggle.value === false &&
      user.value.status === UserStatusEnum.ACTIVE) ||
    username.value != user.value.username
  );
});

/**
 * Properties
 */
const props = __props;
const { show, user } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function onValid() {
  qInputUsernameRef.value?.validate();

  if (qInputUsernameRef.value?.hasError()) {
    return;
  }

  loadingDialog.value = true;

  const updatedUser = { ...user.value };
  updatedUser.username = username.value;
  if (user.value?.status !== UserStatusEnum.VALIDATING) {
    updatedUser.status =
      userIsActiveToggle.value === true
        ? UserStatusEnum.ACTIVE
        : UserStatusEnum.INACTIVE;
  } else {
    updatedUser.status = user.value.status
  }

  adminRepository
    .updateUser(updatedUser)
    .then(() => {
      displayNotifSuccess(
        `L'utilisateur '${user.value.username}' a été modifié avec succès.`,
      );
      emits('success');
      emits('close');

      if (user.value.uuid === connectedUser.value?.uuid && sessionManager.currentUser.value != null) {
        sessionManager.currentUser.value.username = updatedUser.username
      }

    })
    .catch((error) => {
      displayDialogError({
        message: error.message ?? 'null',
      });
    })
    .finally(() => {
      loadingDialog.value = false;
    });
}

/**
 * Watch properties
 */
watch(
  () => user.value,
  (newVal) => {
    if (newVal != null) {
      username.value = newVal.username;
      email.value = newVal.email;
      userIsActiveToggle.value = newVal.status === UserStatusEnum.ACTIVE;
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => show.value,
  (newVal) => {
    if (newVal === true) {
      username.value = user.value.username;
      email.value = user.value.email;
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_q_toggle = _resolveComponent("q-toggle")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    "show-dialog": _unref(show),
    title: "Modification",
    "disable-close-button": loadingDialog.value || loadingPassword.value,
    maximized: _unref(isMobile),
    persistent: loadingDialog.value || loadingPassword.value,
    onClose: _cache[5] || (_cache[5] = ($event: any) => (onClose()))
  }, {
    header: _withCtx(() => [
      (_unref(user)?.status !== _unref(UserStatusEnum).VALIDATING && (!connectedUser.value || connectedUser.value?.email !== _unref(user)?.email))
        ? (_openBlock(), _createBlock(_component_q_toggle, {
            key: 0,
            modelValue: userIsActiveToggle.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((userIsActiveToggle).value = $event)),
            "left-label": "",
            color: "black",
            label: userIsActiveToggle.value ? 'Actif' : 'Désactivé',
            "checked-icon": "check",
            "unchecked-icon": "clear",
            class: "set-outline",
            size: "lg",
            disable: loadingDialog.value || loadingPassword.value || isSuperAdmin.value
          }, null, 8 /* PROPS */, ["modelValue", "label", "disable"]))
        : _createCommentVNode("v-if", true)
    ]),
    content: _withCtx(() => [
      _createVNode(_component_q_card_section, null, {
        default: _withCtx(() => [
          _hoisted_1,
          _hoisted_2
        ]),
        _: 1 /* STABLE */
      }),
      _hoisted_3,
      _createCommentVNode(" Username "),
      _createVNode(_component_q_card_section, null, {
        default: _withCtx(() => [
          _createVNode(QInputUsername, {
            modelValue: username.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((username).value = $event)),
            ref_key: "qInputUsernameRef",
            ref: qInputUsernameRef,
            label: _unref($t)('form.username.label'),
            disable: loadingDialog.value
          }, null, 8 /* PROPS */, ["modelValue", "label", "disable"]),
          _createCommentVNode(" Email "),
          _createVNode(QInputEmail, {
            modelValue: email.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((email).value = $event)),
            ref_key: "qInputEmailRef",
            ref: qInputEmailRef,
            label: _unref($t)('form.email.label'),
            disable: true
          }, null, 8 /* PROPS */, ["modelValue", "label"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_separator),
      _createVNode(_component_q_card_actions, { align: "right" }, {
        default: _withCtx(() => [
          _createVNode(QBtnCancel, {
            label: 'Fermer',
            onClick: _cache[3] || (_cache[3] = ($event: any) => (onClose())),
            disable: loadingDialog.value || loadingPassword.value
          }, null, 8 /* PROPS */, ["disable"]),
          _createVNode(QBtnValid, {
            label: 'Modifier',
            onClick: _cache[4] || (_cache[4] = ($event: any) => (onValid())),
            disable: loadingDialog.value || loadingPassword.value || !updatesMade.value,
            loading: loadingDialog.value,
            "loading-label": '',
            title: !updatesMade.value ? 'Aucune modification détectée' : undefined
          }, null, 8 /* PROPS */, ["disable", "loading", "title"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "disable-close-button", "maximized", "persistent"]))
}
}

})